import React, {
  useState,
  useMemo,
  useRef,
  useEffect,
  useCallback,
} from "react";
import { pageRootTestIds } from "../../testing/pageRootTestIds";
import {
  format,
  subDays,
  startOfDay,
  endOfDay,
  differenceInDays,
} from "date-fns";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate } from "react-router-dom";
import EntityConstants from "../../configs/EntityConstants";
import { GET } from "../../service/CRUDService";
import Toast from "../../common/Toast/Toast";
import CfLoader from "../../common/CfLoader/CfLoader";
import greenTick from "../../resources/icons/greenTick.svg";
import warningInfoIcon from "../../resources/icons/warning-info.svg";
import { CalendarDays, FileText, BarChart2 } from "lucide-react";
import RupeeIcon from "../../resources/icons/rupee-circular-green.svg";
import FilesIcon from "../../resources/icons/files-stack.svg";
import revokedIcon from "../../resources/icons/revoked-lottie-icon.svg";
import { useTranslation } from "react-i18next";

const ApprovalDashboard = () => {
  const navigate = useNavigate();
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const pieChartRef = useRef(null);
  const pieChartInstance = useRef(null);
  const { t, i18n } = useTranslation();

  const [dateRange, setDateRange] = useState("last7days");
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [chartKey, setChartKey] = useState(0);
  const [dashboardStats, setDashboardStats] = useState({
    total_collections: "0",
    currency: "INR",
    total_verifications: "0",
  });
  const [approvalStatusData, setApprovalStatusData] = useState([
    {
      status: "Pending",
      displayLabel: t("dashboard:pending"),
      count: 0,
      color: "#ff9900",
    },
    {
      status: "Approved",
      displayLabel: t("dashboard:approved"),
      count: 0,
      color: "#17a34a",
    },
    {
      status: "Rejected",
      displayLabel: t("dashboard:rejected"),
      count: 0,
      color: "#dc3911",
    },
  ]);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState({
    stats: false,
    approvalStatus: false,
    chartData: false,
  });
  const [limit] = useState(10);
  const [respContentList, setRespContentList] = useState([]);
  const [previous, setPrevious] = useState(null);
  const [next, setNext] = useState(null);
  const [search] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [isSearched, setIsSearched] = useState(false);
  const [offset, setOffset] = useState(0);
  const [initialLoad, setInitialLoad] = useState(true);
  const hasInitializedDateRangeEffect = useRef(false);
  const hasInitializedLanguageEffect = useRef(false);

  const formatRupees = (paisa) => {
    const rupees = paisa / 100;
    return "₹" + rupees.toLocaleString("en-IN");
  };

  const hasAreaChartData = useMemo(() => {
    if (!chartData || chartData.length === 0) return false;
    const total = chartData.reduce(
      (acc, item) => acc + item.collections + item.verifications,
      0,
    );
    return total > 0 ? formatRupees(total) : false;
  }, [chartData]);

  const hasPieChartData = useMemo(() => {
    if (!approvalStatusData || approvalStatusData.length === 0) return false;
    const totalCount = approvalStatusData.reduce(
      (acc, item) => acc + item.count,
      0,
    );
    return totalCount > 0;
  }, [approvalStatusData]);

  const isDateRangeOneWeekOrLess = useMemo(() => {
    switch (dateRange) {
      case "today":
      case "yesterday":
      case "last7days":
        return true;
      case "last30days":
        return false;
      case "custom":
        if (customStartDate && customEndDate) {
          return differenceInDays(customEndDate, customStartDate) < 7;
        }
        return false;
      default:
        return false;
    }
  }, [dateRange, customStartDate, customEndDate]);

  const formatDateForAPI = (date) => {
    if (!date) return "";
    return new Date(date).toISOString();
  };

  const getDateRangeForAPI = useCallback(() => {
    const now = new Date();
    let startDate, endDate;

    const getDateRange = () => {
      switch (dateRange) {
        case "today":
          startDate = startOfDay(now);
          endDate = endOfDay(now);
          break;
        case "yesterday": {
          const yesterday = subDays(now, 1);
          startDate = startOfDay(yesterday);
          endDate = endOfDay(yesterday);
          break;
        }
        case "last7days":
          startDate = subDays(startOfDay(now), 6);
          endDate = endOfDay(now);
          break;
        case "last30days":
          startDate = subDays(startOfDay(now), 29);
          endDate = endOfDay(now);
          break;
        case "custom":
          if (customStartDate && customEndDate) {
            startDate = startOfDay(customStartDate);
            endDate = endOfDay(customEndDate);
          } else {
            startDate = subDays(startOfDay(now), 6);
            endDate = endOfDay(now);
          }
          break;
        default:
          startDate = subDays(startOfDay(now), 6);
          endDate = endOfDay(now);
      }
    };

    getDateRange();

    return {
      from: formatDateForAPI(startDate),
      to: formatDateForAPI(endDate),
    };
  }, [dateRange, customStartDate, customEndDate]);

  const fetchDashboardStats = useCallback(async () => {
    setLoading((prev) => ({ ...prev, stats: true }));
    const { from, to } = getDateRangeForAPI();

    try {
      const response = await GET(
        `${EntityConstants.GET_VERIFICATION_DASHBOARD_STATS}?from=${from}&to=${to}`,
      );
      const data = await response.json();

      if (data) {
        setDashboardStats(data);
      }
    } catch (error) {
      console.error("Error fetching dashboard stats:", error);
      Toast({
        type: "error",
        title: t("dashboard:error"),
        message: t("dashboard:failed_load_dashboard_statistics"),
      });
    } finally {
      setLoading((prev) => ({ ...prev, stats: false }));
    }
  }, [getDateRangeForAPI, t]);

  const fetchApprovalStatus = useCallback(async () => {
    setLoading((prev) => ({ ...prev, approvalStatus: true }));
    const { from, to } = getDateRangeForAPI();

    try {
      const response = await GET(
        `${EntityConstants.GET_VERIFICATION_DASHBOARD_APPROVAL_STATUS}?from=${from}&to=${to}`,
      );
      const data = await response.json();

      if (data) {
        setApprovalStatusData([
          {
            status: "Pending",
            displayLabel: t("dashboard:pending"),
            count: data.pending || 0,
            color: "#ff9900",
          },
          {
            status: "Approved",
            displayLabel: t("dashboard:approved"),
            count: data.approved || 0,
            color: "#17a34a",
          },
          {
            status: "Rejected",
            displayLabel: t("dashboard:rejected"),
            count: data.rejected || 0,
            color: "#dc3911",
          },
        ]);
      }
    } catch (error) {
      console.error("Error fetching approval status:", error);
      Toast({
        type: "error",
        title: t("dashboard:error"),
        message: t("dashboard:failed_load_approval_status"),
      });
    } finally {
      setLoading((prev) => ({ ...prev, approvalStatus: false }));
    }
  }, [getDateRangeForAPI, t]);

  const fetchChartData = useCallback(async () => {
    setLoading((prev) => ({ ...prev, chartData: true }));
    const { from, to } = getDateRangeForAPI();

    try {
      const response = await GET(
        `${EntityConstants.GET_VERIFICATION_DASHBOARD_CHART_DATA}?from=${from}&to=${to}`,
      );
      const data = await response.json();

      if (data && data.data) {
        setChartData(data.data);
      }
    } catch (error) {
      console.error("Error fetching chart data:", error);
      Toast({
        type: "error",
        title: t("dashboard:error"),
        message: t("dashboard:failed_load_chart_data"),
      });
    } finally {
      setLoading((prev) => ({ ...prev, chartData: false }));
    }
  }, [getDateRangeForAPI, t]);

  const fetchDashboardData = useCallback(async () => {
    setChartKey((prev) => prev + 1);
    await Promise.all([
      fetchDashboardStats(),
      fetchApprovalStatus(),
      fetchChartData(),
    ]);
  }, [fetchDashboardStats, fetchApprovalStatus, fetchChartData]);

  const destroyCharts = useCallback(() => {
    if (chartInstance.current) {
      try {
        chartInstance.current.destroy();
      } catch (error) {
        console.warn("Error destroying area chart:", error);
      }
      chartInstance.current = null;
    }

    if (pieChartInstance.current) {
      try {
        pieChartInstance.current.destroy();
      } catch (error) {
        console.warn("Error destroying pie chart:", error);
      }
      pieChartInstance.current = null;
    }
  }, []);

  // Area Chart Effect
  useEffect(() => {
    if (chartInstance.current) {
      try {
        chartInstance.current.destroy();
      } catch (error) {
        console.warn("Error destroying area chart:", error);
      }
      chartInstance.current = null;
    }

    if (!hasAreaChartData || loading.chartData) {
      return;
    }

    import("apexcharts")
      .then((ApexChartsModule) => {
        const ApexCharts = ApexChartsModule.default;

        if (!chartRef.current) return;

        const adjustedData =
          chartData.length === 1
            ? [
                { date: chartData[0].date, collections: 0, verifications: 0 },
                ...chartData,
              ]
            : chartData;

        const options = {
          series: [
            {
              name: t("dashboard:collections"),
              type: "area",
              data: adjustedData.map((item) => item.collections / 100),
            },
            {
              name: t("dashboard:verifications"),
              type: "area",
              data: adjustedData.map((item) => item.verifications),
            },
          ],
          chart: {
            height: 390,
            type: "area",
            toolbar: {
              show: false,
            },
            zoom: {
              enabled: false,
            },
            events: {
              legendClick: function () {
                return false;
              },
            },
          },
          stroke: {
            curve: "smooth",
          },
          fill: {
            type: "gradient",
            gradient: {
              shadeIntensity: 0,
              opacityFrom: 0.7,
              opacityTo: 0.3,
              stops: [0, 90, 100],
            },
          },
          labels: adjustedData.map((item) => {
            const [day, month, year] = item.date.split("-");
            const dateObj = new Date(`${year}-${month}-${day}`);
            return format(dateObj, "do MMM");
          }),
          xaxis: {
            type: "category",
            axisBorder: {
              show: false,
            },
            axisTicks: {
              show: false,
            },
            labels: {
              show: isDateRangeOneWeekOrLess,
            },
          },
          yaxis: [
            {
              axisTicks: { show: false },
              axisBorder: { show: false },
              title: { text: t("dashboard:collections_currency") },
            },
            {
              opposite: true,
              title: { text: t("dashboard:verifications_label") },
            },
          ],
          tooltip: {
            shared: true,
            intersect: false,
            y: {
              formatter: function (y, { seriesIndex }) {
                if (seriesIndex === 0) {
                  return "₹" + y.toLocaleString("en-IN");
                }
                return y;
              },
            },
          },
          legend: {
            position: "top",
            horizontalAlign: "right",
            offsetY: -10,
            onItemClick: {
              toggleDataSeries: false,
            },
            onItemHover: {
              highlightDataSeries: true,
            },
          },
          grid: {
            borderColor: "#f0f0f0",
            strokeDashArray: 3,
          },
          colors: ["#00e396", "#008efa"],
          dataLabels: {
            enabled: isDateRangeOneWeekOrLess,
            formatter: function (val, { seriesIndex }) {
              if (seriesIndex === 0) {
                return "₹" + Math.round(val).toLocaleString("en-IN");
              }
              return Math.round(val);
            },
          },
          states: {
            hover: {
              filter: {
                type: "lighten",
                value: 0.04,
              },
            },
            active: {
              allowMultipleDataPointsSelection: false,
              filter: {
                type: "darken",
                value: 0.35,
              },
            },
          },
        };

        try {
          chartInstance.current = new ApexCharts(chartRef.current, options);
          chartInstance.current.render();
        } catch (error) {
          console.error("Error creating area chart:", error);
        }
      })
      .catch((error) => {
        console.error("Error importing ApexCharts:", error);
      });

    return () => {
      if (chartInstance.current) {
        try {
          chartInstance.current.destroy();
        } catch (error) {
          console.warn("Error in area chart cleanup:", error);
        }
      }
    };
  }, [
    chartData,
    isDateRangeOneWeekOrLess,
    hasAreaChartData,
    loading.chartData,
    chartKey,
    t,
  ]);

  // Pie Chart Effect
  useEffect(() => {
    if (pieChartInstance.current) {
      try {
        pieChartInstance.current.destroy();
      } catch (error) {
        console.warn("Error destroying pie chart:", error);
      }
      pieChartInstance.current = null;
    }

    if (!hasPieChartData || loading.approvalStatus) {
      return;
    }

    import("apexcharts")
      .then((ApexChartsModule) => {
        const ApexCharts = ApexChartsModule.default;

        if (!pieChartRef.current) return;

        const pieOptions = {
          series: approvalStatusData.map((item) => item.count),
          // Use displayLabel for UI, but status for backend operations
          labels: approvalStatusData.map((item) => item.displayLabel),
          colors: approvalStatusData.map((item) => item.color),
          chart: {
            type: "pie",
            width: "100%",
            height: 410,
            animations: {
              enabled: true,
              easing: "easeinout",
              speed: 800,
            },
            events: {
              dataPointSelection: function (event, chartContext, config) {
                event.preventDefault();

                const statusIndex = config.dataPointIndex;
                const status = approvalStatusData[statusIndex].status;

                requestAnimationFrame(() => {
                  navigate("/issuer/approvals/all", {
                    state: {
                      filters: {
                        approvalStatus:
                          status === "Pending" ? "Approval Pending" : status,
                        offset: 0,
                        search: "",
                        amountStatus: null,
                        verifierOrg: null,
                      },
                    },
                  });
                });
              },
            },
          },
          fill: {
            type: "gradient",
            gradient: {
              shade: "light",
              type: "diagonal1",
              shadeIntensity: 0.3,
              gradientToColors: approvalStatusData.map((item) => item.color),
              inverseColors: false,
              opacityFrom: 0.7,
              opacityTo: 0.9,
              stops: [0, 90, 100],
            },
          },
          legend: {
            position: "bottom",
            horizontalAlign: "center",
            markers: {
              radius: 12,
            },
          },
          plotOptions: {
            pie: {
              donut: {
                size: "100%",
              },
            },
          },
          stroke: {
            show: true,
            width: 3,
            colors: "#fff",
          },
          tooltip: {
            y: {
              formatter: function (value) {
                return value.toFixed(1) + "%";
              },
            },
          },
          dataLabels: {
            enabled: true,
            dropShadow: {
              enabled: true,
              top: 1,
              left: 1,
              blur: 1,
              opacity: 0.7,
            },
            style: {
              fontSize: "14px",
              fontWeight: "600",
            },
          },
          responsive: [
            {
              breakpoint: 480,
              options: {
                chart: { width: 200 },
                legend: { position: "bottom" },
              },
            },
          ],
        };

        try {
          pieChartInstance.current = new ApexCharts(
            pieChartRef.current,
            pieOptions,
          );
          pieChartInstance.current.render();
        } catch (error) {
          console.error("Error creating pie chart:", error);
        }
      })
      .catch((error) => {
        console.error("Error importing ApexCharts:", error);
      });

    return () => {
      if (pieChartInstance.current) {
        try {
          pieChartInstance.current.destroy();
        } catch (error) {
          console.warn("Error in pie chart cleanup:", error);
        }
      }
    };
  }, [
    approvalStatusData,
    navigate,
    hasPieChartData,
    loading.approvalStatus,
    chartKey,
    t,
  ]);

  const GET_VERIFICATION_APPROVALS_LIST = useCallback(
    (limit, currentOffset) => {
      setIsSearching(true);
      let apiUrl = `${EntityConstants.GET_VERIFICATION_APPROVALS_LIST}?limit=${limit}&offset=${currentOffset}`;

      if (search) {
        apiUrl += `&search=${search}`;
      }

      GET(apiUrl)
        .then((response) => response.json())
        .then((data) => {
          if (data.error) {
            Toast({
              type: "error",
              title: t("dashboard:error"),
              message: data.error,
            });
          } else {
            setRespContentList(data.results);
            setPrevious(data.previous);
            setNext(data.next);
          }
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          setIsSearching(false);
          setIsSearched(true);
        });
    },
    [search, t],
  );

  // Initial load effect
  useEffect(() => {
    if (initialLoad) {
      fetchDashboardData();
      setInitialLoad(false);
    }
  }, [initialLoad, fetchDashboardData]);

  // Pending approvals pagination effect
  useEffect(() => {
    GET_VERIFICATION_APPROVALS_LIST(limit, offset);
  }, [GET_VERIFICATION_APPROVALS_LIST, limit, offset]);

  // Date range change effect
  useEffect(() => {
    if (!hasInitializedDateRangeEffect.current) {
      hasInitializedDateRangeEffect.current = true;
      return;
    }
    fetchDashboardData();
  }, [
    dateRange,
    customStartDate,
    customEndDate,
    fetchDashboardData,
  ]);

  // Language change effect
  useEffect(() => {
    if (!hasInitializedLanguageEffect.current) {
      hasInitializedLanguageEffect.current = true;
      return;
    }
    // Update approval status data with new translations
    setApprovalStatusData((prev) => [
      { ...prev[0], displayLabel: t("dashboard:pending") },
      { ...prev[1], displayLabel: t("dashboard:approved") },
      { ...prev[2], displayLabel: t("dashboard:rejected") },
    ]);

    // Trigger chart re-render
    setChartKey((prev) => prev + 1);
  }, [i18n.language, t]);

  // Cleanup effect
  useEffect(() => {
    return () => {
      destroyCharts();
    };
  }, [destroyCharts]);

  const handlePrevious = () => {
    if (!previous) return;
    setOffset((currentOffset) => Math.max(currentOffset - limit, 0));
  };

  const handleNext = () => {
    if (!next) return;
    setOffset((currentOffset) => currentOffset + limit);
  };

  const handleCardClick = (transaction) => {
    const currentFilters = {
      search,
      offset,
    };
    const status =
      transaction.verification_approval_status || "Approval Pending";

    navigate(`/issuer/approvals/${transaction.transaction_id}`, {
      state: {
        ...transaction,
        filters: currentFilters,
        status,
      },
    });
  };

  const handleCardKeyDown = (event, transaction) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleCardClick(transaction);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return t("dashboard:no_date_available");
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      return t("dashboard:invalid_date");
    }
  };

  const DateFilterDropdown = () => {
    const tid = "approval-dashboard-date-filter";
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const [tempStartDate, setTempStartDate] = useState(customStartDate);
    const [tempEndDate, setTempEndDate] = useState(customEndDate);

    const today = useMemo(() => new Date(), []);

    const minStartDate = useMemo(() => {
      const minDate = new Date(today);
      minDate.setDate(minDate.getDate() - 180);
      return minDate;
    }, [today]);

    const maxEndDate = useMemo(() => {
      if (!tempStartDate) return today;

      const maxFromStartDate = new Date(tempStartDate);
      maxFromStartDate.setDate(maxFromStartDate.getDate() + 90);

      return maxFromStartDate < today ? maxFromStartDate : today;
    }, [tempStartDate, today]);

    const minEndDate = useMemo(() => {
      return tempStartDate || minStartDate;
    }, [tempStartDate, minStartDate]);

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target)
        ) {
          setIsOpen(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const presetOptions = [
      { label: t("dashboard:today"), value: "today" },
      { label: t("dashboard:yesterday"), value: "yesterday" },
      { label: t("dashboard:last_7_days"), value: "last7days" },
      { label: t("dashboard:last_30_days"), value: "last30days" },
    ];

    const handlePresetSelect = (preset) => {
      setDateRange(preset);
      setCustomStartDate(null);
      setCustomEndDate(null);
      setIsOpen(false);
    };

    const handleApplyCustomDate = () => {
      if (tempStartDate && tempEndDate && tempEndDate >= tempStartDate) {
        setCustomStartDate(tempStartDate);
        setCustomEndDate(tempEndDate);
        setDateRange("custom");
        setIsOpen(false);
      }
    };

    const getDisplayText = () => {
      if (dateRange === "custom" && customStartDate && customEndDate) {
        return `${format(customStartDate, "MMM d, yyyy")} - ${format(
          customEndDate,
          "MMM d, yyyy",
        )}`;
      }
      return (
        presetOptions.find((opt) => opt.value === dateRange)?.label ||
        t("dashboard:select_date_range")
      );
    };

    return (
      <div
        className="relative inline-block text-left"
        ref={dropdownRef}
        id={tid}
      >
        <button
          type="button"
          id={`${tid}-trigger`}
          className="inline-flex justify-between items-center w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none"
          onClick={() => setIsOpen(!isOpen)}
        >
          {getDisplayText()}
          <svg
            className="-mr-1 ml-2 h-5 w-5"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>

        {isOpen && (
          <div
            className="origin-top-left absolute left-0 mt-2 w-[23rem] rounded-lg shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50"
            id={`${tid}-menu`}
          >
            <div className="" role="menu" aria-orientation="vertical">
              {presetOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  id={`${tid}-preset-${option.value}`}
                  className={`${
                    dateRange === option.value
                      ? "bg-gray-100 text-gray-900"
                      : "text-gray-700"
                  } block w-full text-left px-4 py-2 text-sm hover:bg-gray-100`}
                  onClick={() => handlePresetSelect(option.value)}
                >
                  {option.label}
                </button>
              ))}

              <hr className=" border-gray-200" />

              <div className="px-4 py-2 text-sm text-gray-500 font-medium">
                {t("dashboard:custom_period")}
              </div>

              <div className="px-4 py-2 space-y-2">
                <div className="flex items-center space-x-3">
                  <div
                    className="relative w-[10rem]"
                    id={`${tid}-datepicker-start`}
                  >
                    <DatePicker
                      selected={tempStartDate}
                      onChange={(date) => {
                        setTempStartDate(date);

                        if (!date) {
                          setTempEndDate(null);
                          return;
                        }

                        if (tempEndDate && tempEndDate < date) {
                          setTempEndDate(null);
                          return;
                        }

                        const maxFromStart = new Date(date);
                        maxFromStart.setDate(maxFromStart.getDate() + 90);
                        const upperBound =
                          maxFromStart < today ? maxFromStart : today;

                        if (tempEndDate && tempEndDate > upperBound) {
                          setTempEndDate(null);
                        }
                      }}
                      selectsStart
                      startDate={tempStartDate}
                      endDate={tempEndDate}
                      dateFormat={"dd/MM/yyyy"}
                      className="w-full border border-gray-300 rounded pl-12 pr-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                      placeholderText={t("dashboard:start_date")}
                      minDate={minStartDate}
                      maxDate={today}
                      showYearDropdown
                      showMonthDropdown
                      dropdownMode="select"
                      yearDropdownItemNumber={15}
                      scrollableYearDropdown
                    />
                    <CalendarDays className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500 pointer-events-none" />
                  </div>

                  <div
                    className="relative w-[10rem]"
                    id={`${tid}-datepicker-end`}
                  >
                    <DatePicker
                      selected={tempEndDate}
                      onChange={(date) => setTempEndDate(date)}
                      selectsEnd
                      startDate={tempStartDate}
                      endDate={tempEndDate}
                      dateFormat={"dd/MM/yyyy"}
                      minDate={minEndDate}
                      maxDate={maxEndDate}
                      className="w-full border border-gray-300 rounded pl-12 pr-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                      placeholderText={t("dashboard:end_date")}
                      disabled={!tempStartDate}
                      showYearDropdown
                      showMonthDropdown
                      dropdownMode="select"
                      yearDropdownItemNumber={15}
                      scrollableYearDropdown
                    />
                    <CalendarDays className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500 pointer-events-none" />
                  </div>
                </div>
                {!tempStartDate ? (
                  <div className="text-xs text-gray-500">
                    {t("dashboard:select_start_date_between")}{" "}
                    {format(minStartDate, "MMM d, yyyy")} {t("dashboard:and")}{" "}
                    {format(today, "MMM d, yyyy")}
                  </div>
                ) : (
                  <div className="text-xs text-gray-500">
                    {t("dashboard:end_date_must_be_between")}{" "}
                    {format(tempStartDate, "MMM d, yyyy")} {t("dashboard:and")}{" "}
                    {format(today, "MMM d, yyyy")} {t("dashboard:max_90_days")}
                  </div>
                )}

                <button
                  type="button"
                  id={`${tid}-apply`}
                  onClick={handleApplyCustomDate}
                  disabled={
                    !tempStartDate ||
                    !tempEndDate ||
                    tempEndDate < tempStartDate
                  }
                  className="w-full bg-cfPrimaryButton text-white font-medium py-2 px-4 rounded-md text-sm hover:bg-cfPrimaryDark disabled:opacity-50 disabled:cursor-not-allowed mt-3"
                >
                  {t("dashboard:apply")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const NoDataPlaceholder = ({ height }) => (
    <div
      className={`flex flex-col justify-center items-center text-gray-500`}
      style={{ height: `${height}px` }}
    >
      <BarChart2 className="w-12 h-12 mb-2 text-gray-300" />
      <p className="font-medium">{t("dashboard:no_data_available")}</p>
      <p className="text-sm text-gray-400">
        {t("dashboard:try_different_date_range")}
      </p>
    </div>
  );

  return (
    <div
      className="mt-20 bg-cfSecondary min-h-screen p-6"
      id={pageRootTestIds.approvalDashboard}
    >
      <div className="mx-auto space-y-6">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          <div className="flex flex-col lg:flex-row items-start lg:items-center gap-2 w-full lg:w-auto">
            <DateFilterDropdown />
          </div>
        </div>

        {/* KPI's */}
        <div className="w-full flex flex-col xl:flex-row gap-5">
          <div className="flex flex-col gap-6 w-full xl:w-[65%]">
            {/* Actual KPI's */}
            <div className="flex flex-col md:flex-row gap-5">
              {/* KPI 1 */}
              <div className="relative flex flex-col w-full min-w-0 break-words bg-white shadow-xl border border-gray-200 rounded-2xl bg-clip-border">
                <div className="flex-auto py-4">
                  <div className="flex flex-row">
                    <div className="pl-3 text-right basis-0/4">
                      <div className="inline-block w-12 h-12 text-center rounded-full bg-green-100 flex items-center justify-center">
                        <img
                          src={RupeeIcon}
                          alt="Collection Icon"
                          className="w-6 h-6"
                        />
                      </div>
                    </div>
                    {loading.stats ? (
                      <div className="flex-none w-2/3 max-w-full px-3">
                        <div className="flex items-center gap-3 animate-pulse">
                          <div>
                            <div className="h-8 w-52 bg-gray-200 rounded-md mb-2"></div>
                            <div className="h-4 w-32 bg-gray-200 rounded-md"></div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="flex-none w-2/3 max-w-full px-3">
                        <div className="flex items-center gap-3">
                          <div>
                            <h5 className="mb-2 font-semibold text-gray-900 text-4xl">
                              {formatRupees(dashboardStats.total_collections)}
                            </h5>
                            <p className="mb-0 text-gray-600 text-sm font-light">
                              {t("dashboard:total_collections")}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* KPI 2 */}
              <div className="relative flex flex-col w-full min-w-0 break-words bg-white shadow-xl border border-gray-200 rounded-2xl bg-clip-border">
                <div className="flex-auto py-4">
                  <div className="flex flex-row">
                    <div className="pl-3 text-right basis-0/4">
                      <div className="inline-block w-12 h-12 text-center rounded-full bg-blue-100 flex items-center justify-center">
                        <img
                          src={FilesIcon}
                          alt="Total Verifications"
                          className="w-7 h-7"
                        />
                      </div>
                    </div>
                    {loading.stats ? (
                      <div className="flex-none w-2/3 max-w-full px-3">
                        <div className="flex items-center gap-3 animate-pulse">
                          <div>
                            <div className="h-8 w-52 bg-gray-200 rounded-md mb-2"></div>
                            <div className="h-4 w-32 bg-gray-200 rounded-md"></div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="flex-none w-2/3 max-w-full px-3">
                        <div className="flex items-center gap-3">
                          <div>
                            <h5 className="mb-2 font-semibold text-gray-900 text-4xl">
                              {dashboardStats.total_verifications}
                            </h5>
                            <p className="mb-0 text-gray-600 text-sm font-light">
                              {t("dashboard:total_verifications")}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {/* Area Line Chart */}
            <div className="rounded-xl bg-white shadow border border-gray-200 pt-8 flex-1">
              {loading.chartData ? (
                <div className="flex justify-center items-center h-[390px]">
                  {t("dashboard:loading")}
                </div>
              ) : hasAreaChartData ? (
                <div id="chart" ref={chartRef} className="h-full"></div>
              ) : (
                <NoDataPlaceholder height={390} />
              )}
            </div>
          </div>

          {/* Pie Chart */}
          <div className="w-full xl:w-[35%] flex flex-col shadow-xl border border-gray-200 rounded-2xl bg-clip-border bg-white p-6">
            <div className="mb-2">
              <h3 className="text-lg font-semibold text-gray-800">
                {t("dashboard:approval_status_overview")}
              </h3>
              <p className="text-xs text-gray-600">
                {t("dashboard:distribution_verification_requests")}
              </p>
            </div>
            {loading.approvalStatus ? (
              <div className="flex justify-center items-center h-[410px]">
                {t("dashboard:loading")}
              </div>
            ) : hasPieChartData ? (
              <div ref={pieChartRef} id="pie-chart" className="flex-1"></div>
            ) : (
              <NoDataPlaceholder height={410} />
            )}
            <hr className="my-4 border-gray-200" />
            <div className="flex items-center justify-center w-full mt-auto">
              <button
                id="approval-dashboard-view-all"
                className="px-5 py-2.5 text-sm cursor-pointer font-medium text-white inline-flex items-center bg-cfPrimaryButton hover:bg-cfPrimaryDark focus:outline-none rounded-[8px] text-center"
                onClick={() => navigate("/issuer/approvals/all")}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    navigate("/issuer/approvals/all");
                  }
                }}
                tabIndex={0}
              >
                <FileText className="w-4 h-4 mr-2" />
                {t("dashboard:view_all")}
              </button>
            </div>
          </div>
        </div>

        {/* Pending approval Table */}
        <div className="shadow-xl border border-gray-200 rounded-2xl bg-clip-border bg-white pb-[1.5rem]">
          <div className="overflow-x-auto">
            <div className="text-lg font-semibold text-gray-800 p-[1.5rem]">
              {t("dashboard:pending_approvals")}
              <p className="text-xs text-gray-600 font-normal">
                {t("dashboard:certificates_review")}
              </p>
            </div>

            <div className="flex flex-col justify-center gap-2 min-w-[90.5rem] md:w-full">
              <div className="flex bg-cfTableHeaderBackgroundColor items-center h-[4rem] border text-center min-w-fit px-6">
                <div className="px-6 py-3 text-xs font-semibold text-gray-800 uppercase tracking-wider w-1/5">
                  {t("dashboard:verifier_name")}
                </div>
                <div className="px-6 py-3 text-xs font-semibold text-gray-800 uppercase tracking-wider w-1/5">
                  {t("dashboard:verifier_organisation")}
                </div>
                <div className="px-6 py-3 text-xs font-semibold text-gray-800 uppercase tracking-wider w-1/5">
                  {t("dashboard:created_at")}
                </div>
                <div className="px-6 py-3 text-xs font-semibold text-gray-800 uppercase tracking-wider w-1/5">
                  {t("dashboard:credissuer_verified")}
                </div>
                <div className="px-6 py-3 text-xs font-semibold text-gray-800 uppercase tracking-wider w-1/5">
                  {t("dashboard:amount_paid")}
                </div>
                <div className="px-6 py-3 text-xs font-semibold text-gray-800 uppercase tracking-wider w-1/5">
                  {t("dashboard:document_type")}
                </div>
              </div>

              {isSearching && (
                <div className="flex justify-center items-center min-h-[8rem] mt-16">
                  <CfLoader />
                </div>
              )}
              {!isSearching && isSearched && respContentList.length === 0 && (
                <tr>
                  <td
                    colSpan="6"
                    className="flex justify-center items-center min-h-[8rem] "
                  >
                    {t("dashboard:no_results_found")}
                  </td>
                </tr>
              )}
              {!isSearching &&
                respContentList.map((transaction) => (
                  <div
                    key={transaction.transaction_id}
                    id={`approval-dashboard-pending-row-${transaction.transaction_id}`}
                    className="flex bg-white items-center min-h-[4rem] border-b py-1 px-4 cursor-pointer"
                    onClick={() => handleCardClick(transaction)}
                    onKeyDown={(e) => handleCardKeyDown(e, transaction)}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="w-1/6 text-center min-w-[150px] flex-shrink-0 overflow-hidden">
                      <p className="font-regular text-black text-[14px]">
                        {transaction.verifier_name ||
                          t("dashboard:not_applicable")}
                      </p>
                    </div>

                    <div className="w-1/6 text-center min-w-[150px] flex-shrink-0 overflow-hidden">
                      <p className="font-regular text-black text-[14px]">
                        {transaction.verifier_org_name ||
                          t("dashboard:not_applicable")}
                      </p>
                    </div>
                    <div className="w-1/6 text-center min-w-[150px] flex-shrink-0 overflow-hidden">
                      <p className="font-regular text-black text-[14px]">
                        {formatDate(transaction.created_at)}
                      </p>
                    </div>
                    <div className="w-1/6 text-center min-w-[150px] flex overflow-hidden items-center justify-center">
                      <p className="font-regular text-black text-[14px]">
                        {transaction.vc_status === "Verified" ? (
                          <img
                            src={greenTick}
                            alt="Verified"
                            className="w-10 h-10"
                          />
                        ) : transaction.vc_status === "Revoked" ? (
                          <img
                            src={revokedIcon}
                            alt="Not Verified"
                            className="w-11 h-11"
                          />
                        ) : (
                          <img
                            src={warningInfoIcon}
                            alt="Not Verified"
                            className="w-11 h-11"
                          />
                        )}
                      </p>
                    </div>
                    <div className="w-1/6 text-center min-w-[150px] flex-shrink-0 overflow-hidden">
                      <p className="font-regular text-black text-[14px]">
                        {transaction.verification_cost &&
                        parseFloat(transaction.verification_cost) !== 0
                          ? `₹ ${(
                              parseFloat(transaction.verification_cost) / 100
                            ).toFixed(2)}`
                          : t("dashboard:free_of_charge")}
                      </p>
                    </div>
                    <div className="w-1/6 text-center min-w-[150px] flex-shrink-0 overflow-hidden">
                      <p className="font-regular text-black text-[14px]">
                        {transaction.document_type ||
                          t("dashboard:not_applicable")}
                      </p>
                    </div>
                  </div>
                ))}
            </div>
          </div>
          {(previous || next) && (
            <div className="flex items-center justify-between mt-6 px-8 w-full">
              <button
                onClick={handlePrevious}
                id="approval-dashboard-pagination-previous"
                className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
                disabled={previous === null}
              >
                <span className="flex items-center justify-center gap-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="w-5 h-5 rtl:-scale-x-100"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6.75 15.75L3 12m0 0l3.75-3.75M3 12h18"
                    />
                  </svg>

                  <span>{t("dashboard:previous")}</span>
                </span>
              </button>

              <button
                onClick={handleNext}
                id="approval-dashboard-pagination-next"
                className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
                disabled={next === null}
              >
                <span className="flex items-center justify-center gap-2">
                  <span>{t("dashboard:next")}</span>

                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="w-5 h-5 rtl:-scale-x-100"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3"
                    />
                  </svg>
                </span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApprovalDashboard;
